/**
 * Appin kausaatiotracer (WP-DT1, app-debug-timeline-plan.md). Puhdas moduuli
 * ilman DOMia ja ilman `window`-kosketusta — runtime.ts kytkee tämän
 * saumoihinsa (setSlot / reEval / lifecycle-actionit / bridge.invoke / klikit)
 * WP-DT2:ssa ja editorin paneeli lukee eventit sillan yli WP-DT3:ssa.
 *
 * **Kausaatiopuu, ei aikajana.** Jokaisella eventillä on `parent`, joten
 * "mikä aiheutti mitä" luetaan rakenteesta eikä aikaleimoista:
 *
 *     user-click save-button          (isRoot)
 *     └─ lifecycle-action show        parent = user-click
 *     └─ api-start save-order         parent = user-click
 *        └─ api-end 200 (158 ms)      parent = api-start
 *           └─ slot-emit table.data   parent = api-end
 *              └─ binding-eval …      parent = slot-emit
 *
 * Vanhempi kannetaan {@link Tracer.withParent}illa synkronisesti: kutsupino
 * asettaa kontekstin, ja kaikki sen aikana kirjatut eventit perivät sen.
 * Async-rajat (bridge.invoke) kytketään eksplisiittisesti WP-DT2:ssa — tämä
 * moduuli ei tiedä niistä mitään.
 *
 * **Kaksi moodia** (suunnitelman `D2`):
 *   - `metadata` (oletus, aina päällä myös julkaistussa Apissa) — id:t, tyypit,
 *     kohteet ja kevyet diagnostiikkakentät. **Ei koskaan slot-arvoja**, joten
 *     katsojan dataa ei päädy puskuriin.
 *   - `full` — kytkeytyy vain editorin ohjausviestillä; lisää `payload`in ja
 *     `prevValue`n syväkopioina.
 */

/** Eventtityypit. Juurityyppejä ovat `mount`, `user-click` ja `form-submit`. */
export type TraceEventType =
  | 'mount'
  | 'user-click'
  | 'form-submit'
  | 'lifecycle-action'
  | 'api-start'
  | 'api-end'
  | 'slot-emit'
  | 'binding-eval'
  | 'error';

/** Yksi kirjattu tapahtuma kausaatiopuussa. */
export interface TraceEvent {
  /** Monotoninen juokseva numero — myös {@link Tracer.clear}in yli. */
  id: number;
  /** Aikaleima (`performance.now()`-pohjainen, ms sessionStartTs:stä riippumaton). */
  ts: number;
  /** Kausaatiovanhemman id, tai null kun event on ketjun juuri. */
  parent: number | null;
  type: TraceEventType;
  /** Aiheuttaja: komponentin id, `'page'` tai riippuvuusavain. */
  source: string;
  /** Kohde: `"id.slot"`, datalähteen id, actionin kohteet — tyhjä jos ei ole. */
  target: string;
  /** Slotin nimi (slot-emit / binding-eval). */
  slot?: string;
  /** Aloittaako tämä uuden ketjun. */
  isRoot: boolean;
  /** Lifecycle-actionin operaatio (`show`/`hide`/`show-toast`/…). */
  operation?: string;
  /** Lausekkeen lähdeteksti (binding-eval). */
  expression?: string;
  /** Kesto ms (api-end: aika api-startista). */
  duration?: number;
  /** HTTP-status (api-end). */
  statusCode?: number;
  /** Virheviesti (error). */
  message?: string;
  /** Uusi arvo syväkopiona — **vain full-moodissa**. */
  payload?: unknown;
  /** Edellinen arvo syväkopiona — **vain full-moodissa**. */
  prevValue?: unknown;
}

/** {@link Tracer.record}in valinnaiset kentät. */
export interface TraceExtra {
  isRoot?: boolean;
  slot?: string;
  operation?: string;
  expression?: string;
  duration?: number;
  statusCode?: number;
  message?: string;
  /** Talletetaan vain full-moodissa. */
  payload?: unknown;
  /** Talletetaan vain full-moodissa. */
  prevValue?: unknown;
}

export type TraceMode = 'metadata' | 'full';

/** {@link Tracer.export}in tuottama siirtomuoto (editorin "Vie JSON"). */
export interface TraceExport {
  schemaVersion: string;
  appId: string;
  /** Session alkuhetki samalla kellolla kuin {@link TraceEvent.ts}. */
  sessionStartTs: number;
  exportTs: number;
  mode: TraceMode;
  events: TraceEvent[];
}

export interface Tracer {
  /** Puskurin sisältö vanhimmasta uusimpaan (ei kopio — älä muokkaa). */
  readonly events: readonly TraceEvent[];
  readonly mode: TraceMode;
  /** Session alkuhetki; paneelin suhteelliset aikaleimat lasketaan tästä. */
  readonly sessionStartTs: number;
  /** Nykyinen kausaatiovanhempi (null = seuraava event on juuri). */
  readonly currentParent: number | null;
  /** Kirjaa event. Palauttaa null jos tracer on pois päältä. */
  record(
    type: TraceEventType,
    source: string,
    target?: string,
    extra?: TraceExtra,
  ): TraceEvent | null;
  /** Aja `fn` niin että sen aikana kirjatut eventit saavat vanhemmakseen `parentId`. */
  withParent<T>(parentId: number | null, fn: () => T): T;
  /** Aseta vanhempi ilman kutsupinoa (klikin capture-vaihe → nollaus mikrotaskissa). */
  setCurrentParent(parentId: number | null): void;
  /** Tuottiko event yhtään lasta. Epätosi binding-eval = evaluointi joka ei muuttanut mitään. */
  hasChildren(eventId: number): boolean;
  setMode(mode: TraceMode): void;
  /** Globaali katkaisin (DevTools: `window.fasthub._trace.setEnabled(false)`). */
  setEnabled(enabled: boolean): void;
  /**
   * Tilaa eventit erissä. Erä kootaan mikrotaskiin, joten yhden klikin koko
   * kaskadi tulee yhtenä kutsuna. Palauttaa irrotusfunktion.
   */
  subscribe(onEvents: (batch: TraceEvent[]) => void): () => void;
  /** Tyhjennä puskuri (id-juoksu jatkuu — ks. toteutuksen kommentti). */
  clear(): void;
  export(): TraceExport;
}

export interface TracerOptions {
  /** Appin id export-JSONiin. */
  appId?: string;
  /** Rengaspuskurin katto; rajataan välille 50–2000. */
  maxEvents?: number;
  /** Kello — testit injektoivat deterministisen. */
  now?: () => number;
}

/** Rengaspuskurin oletuskatto (sama kuin referenssitoteutuksessa). */
export const DEFAULT_MAX_EVENTS = 500;
const MIN_MAX_EVENTS = 50;
const MAX_MAX_EVENTS = 2000;

/** Export-muodon versio: kasvaa jos kenttien semantiikka muuttuu. */
export const TRACE_SCHEMA_VERSION = '1.0.0';

function defaultNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/**
 * Syväkopio full-moodin arvoille. `structuredClone` ensin; sen ulkopuolelle
 * jäävät (funktiot, DOM-solmut, proxyt) yritetään JSON-kierrätyksellä, ja jos
 * sekään ei onnistu, arvo jätetään pois. Tracer ei saa koskaan kaataa Appia
 * sen takia että jokin slot sattuu sisältämään ei-serialisoituvan arvon.
 */
function deepClone(value: unknown): unknown {
  if (value == null || typeof value !== 'object') return value;
  try {
    if (typeof structuredClone === 'function') return structuredClone(value);
  } catch {
    // ei-kloonattava (funktio, DOM-solmu, proxy) — kokeile JSONia
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return undefined;
  }
}

export function createTracer(options: TracerOptions = {}): Tracer {
  const now = options.now ?? defaultNow;
  const appId = options.appId ?? '';
  const maxEvents = Math.max(
    MIN_MAX_EVENTS,
    Math.min(MAX_MAX_EVENTS, Math.floor(options.maxEvents ?? DEFAULT_MAX_EVENTS) || DEFAULT_MAX_EVENTS),
  );
  const sessionStartTs = now();

  let events: TraceEvent[] = [];
  let nextId = 1;
  let mode: TraceMode = 'metadata';
  let currentParent: number | null = null;
  let enabled = true;

  /**
   * parent-id → montako lasta sen alle on kirjattu. Tämä on koko no-op-
   * suodattimen pohja: binding-eval jonka alle ei tullut yhtään slot-emittiä
   * on evaluointi joka ei muuttanut mitään, ja referenssitoteutuksessa juuri
   * nämä täyttivät näkymän kohinalla.
   *
   * Laskuria EI vähennetä kun lapsi karsiutuu puskurista — kysymys on "tuottiko
   * tämä event aikanaan lapsia", ei "montako lasta on tallella".
   */
  const childCounts = new Map<number, number>();

  const subscribers: Array<(batch: TraceEvent[]) => void> = [];
  let pending: TraceEvent[] = [];
  let flushScheduled = false;

  function flush(): void {
    flushScheduled = false;
    if (pending.length === 0) return;
    const batch = pending;
    pending = [];
    // Kopio ennen iterointia: irrotus kesken jakelun ei saa hypätä tilaajan yli.
    for (const fn of [...subscribers]) {
      try {
        fn(batch);
      } catch {
        // Tilaajan virhe ei saa kaataa Appia eikä estää muita tilaajia.
      }
    }
  }

  function scheduleFlush(): void {
    if (flushScheduled || subscribers.length === 0) return;
    flushScheduled = true;
    if (typeof queueMicrotask === 'function') queueMicrotask(flush);
    else void Promise.resolve().then(flush);
  }

  return {
    get events() {
      return events;
    },
    get mode() {
      return mode;
    },
    get sessionStartTs() {
      return sessionStartTs;
    },
    get currentParent() {
      return currentParent;
    },

    record(type, source, target = '', extra) {
      if (!enabled) return null;
      const event: TraceEvent = {
        id: nextId,
        ts: now(),
        parent: currentParent,
        type,
        source: source == null ? '' : String(source),
        target: target == null ? '' : String(target),
        isRoot: extra?.isRoot === true,
      };
      nextId += 1;
      if (extra) {
        if (extra.slot != null) event.slot = String(extra.slot);
        if (extra.operation != null) event.operation = String(extra.operation);
        if (extra.expression != null) event.expression = String(extra.expression);
        if (extra.duration != null) event.duration = Number(extra.duration);
        if (extra.statusCode != null) event.statusCode = Number(extra.statusCode);
        if (extra.message != null) event.message = String(extra.message);
        // Arvot vain full-moodissa: metadata-moodi on päällä myös julkaistussa
        // Apissa, eikä katsojan dataa oteta talteen ilman tekijän nauhoitusta.
        if (mode === 'full') {
          if ('payload' in extra) event.payload = deepClone(extra.payload);
          if ('prevValue' in extra) event.prevValue = deepClone(extra.prevValue);
        }
      }
      if (event.parent != null) {
        childCounts.set(event.parent, (childCounts.get(event.parent) ?? 0) + 1);
      }
      events.push(event);
      if (events.length > maxEvents) {
        const dropped = events.shift();
        // Karsitun eventin lapsilaskuri pois, muuten kartta vuotaa pitkässä
        // sessiossa (referenssin korjaus DT1:n jälkeen).
        if (dropped) childCounts.delete(dropped.id);
      }
      pending.push(event);
      scheduleFlush();
      return event;
    },

    withParent(parentId, fn) {
      const previous = currentParent;
      currentParent = parentId;
      try {
        return fn();
      } finally {
        currentParent = previous;
      }
    },

    setCurrentParent(parentId) {
      currentParent = parentId;
    },

    hasChildren(eventId) {
      return (childCounts.get(eventId) ?? 0) > 0;
    },

    setMode(next) {
      mode = next === 'full' ? 'full' : 'metadata';
    },

    setEnabled(next) {
      enabled = next !== false;
    },

    subscribe(onEvents) {
      subscribers.push(onEvents);
      return () => {
        const at = subscribers.indexOf(onEvents);
        if (at >= 0) subscribers.splice(at, 1);
      };
    },

    clear() {
      events = [];
      childCounts.clear();
      pending = [];
      // nextId EI nollaudu: matkalla oleva erä tai paneelin jo piirtämä rivi
      // viittaisi muuten samaan id:hen kuin tyhjennyksen jälkeinen uusi event.
      // Id:t saavat hypätä — ne ovat avaimia, eivät laskureita.
    },

    export() {
      return {
        schemaVersion: TRACE_SCHEMA_VERSION,
        appId,
        sessionStartTs,
        exportTs: now(),
        mode,
        events: events.map((e) => ({ ...e })),
      };
    },
  };
}
